import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, CircularProgress, FormControlLabel, Grid,
  InputAdornment, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { colorService } from '../../../services/fashionService';
import { FashionColor } from '../../../types/fashion';

/** Paleta sugerida para indumentaria / calzado. */
const PALETA_MODA: { nombre: string; hex: string }[] = [
  { nombre: 'Negro', hex: '#000000' },
  { nombre: 'Blanco', hex: '#FFFFFF' },
  { nombre: 'Gris', hex: '#9E9E9E' },
  { nombre: 'Gris oscuro', hex: '#424242' },
  { nombre: 'Beige', hex: '#D7CCC8' },
  { nombre: 'Crema', hex: '#FFF8E1' },
  { nombre: 'Marrón', hex: '#6D4C41' },
  { nombre: 'Camel', hex: '#C4A484' },
  { nombre: 'Azul marino', hex: '#0D47A1' },
  { nombre: 'Azul', hex: '#1976D2' },
  { nombre: 'Celeste', hex: '#81D4FA' },
  { nombre: 'Verde', hex: '#2E7D32' },
  { nombre: 'Verde oliva', hex: '#556B2F' },
  { nombre: 'Rojo', hex: '#C62828' },
  { nombre: 'Bordó', hex: '#880E4F' },
  { nombre: 'Rosa', hex: '#F48FB1' },
  { nombre: 'Fucsia', hex: '#D81B60' },
  { nombre: 'Naranja', hex: '#EF6C00' },
  { nombre: 'Mostaza', hex: '#F9A825' },
  { nombre: 'Amarillo', hex: '#FDD835' },
  { nombre: 'Violeta', hex: '#7B1FA2' },
  { nombre: 'Lila', hex: '#CE93D8' },
  { nombre: 'Turquesa', hex: '#00897B' },
  { nombre: 'Denim', hex: '#3F51B5' },
];

const HEX_RE = /^#([0-9A-Fa-f]{6})$/;

const normalizeHex = (raw: string): string => {
  let v = (raw || '').trim().toUpperCase();
  if (!v) return '';
  if (!v.startsWith('#')) v = `#${v}`;
  if (/^#[0-9A-F]{3}$/.test(v)) {
    v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  }
  return v;
};

const isValidHex = (hex: string) => !hex || HEX_RE.test(hex);

const ColorForm: React.FC = () => {
  const { codigoColor } = useParams();
  const isEdit = Boolean(codigoColor);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FashionColor>({
    codigoColor: '',
    nombreColor: '',
    codigoHex: '',
    estado: 'A',
  });
  const [hexError, setHexError] = useState<string | null>(null);

  const hexActual = useMemo(() => normalizeHex(form.codigoHex || ''), [form.codigoHex]);
  const hexValido = isValidHex(hexActual);
  const colorPickerValue = hexValido && hexActual ? hexActual : '#1976D2';

  useEffect(() => {
    if (!codigoColor) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await colorService.getColor(codigoColor);
        if (cancelled) return;
        setForm({
          ...data,
          codigoHex: normalizeHex(data.codigoHex || '') || '',
        });
      } catch {
        if (!cancelled) {
          enqueueSnackbar('No se pudo cargar el color', { variant: 'error' });
          navigate('/inventario/fashion/colores');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [codigoColor, enqueueSnackbar, navigate]);

  const aplicarHex = (hex: string, sugerirNombre?: string) => {
    const norm = normalizeHex(hex);
    setForm((f) => ({
      ...f,
      codigoHex: norm,
      // Solo completa el nombre si está vacío
      nombreColor: f.nombreColor.trim() ? f.nombreColor : (sugerirNombre || f.nombreColor),
    }));
    setHexError(norm && !isValidHex(norm) ? 'Usá el formato #RRGGBB' : null);
  };

  const handleHexInput = (raw: string) => {
    let v = raw.toUpperCase();
    if (v && !v.startsWith('#')) v = `#${v.replace(/#/g, '')}`;
    v = v.slice(0, 7);
    setForm((f) => ({ ...f, codigoHex: v }));
    const norm = normalizeHex(v);
    setHexError(v && !isValidHex(norm) ? 'Usá el formato #RRGGBB' : null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.codigoColor.trim() || !form.nombreColor.trim()) {
      enqueueSnackbar('Código y nombre son obligatorios', { variant: 'warning' });
      return;
    }
    const hex = normalizeHex(form.codigoHex || '');
    if (hex && !isValidHex(hex)) {
      setHexError('Usá el formato #RRGGBB');
      enqueueSnackbar('El código hex no es válido', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      if (isEdit && codigoColor) {
        await colorService.updateColor(codigoColor, {
          nombreColor: form.nombreColor.trim(),
          codigoHex: hex || null,
          estado: form.estado,
        });
        enqueueSnackbar('Color actualizado', { variant: 'success' });
      } else {
        await colorService.createColor({
          ...form,
          codigoColor: form.codigoColor.trim().toUpperCase(),
          nombreColor: form.nombreColor.trim(),
          codigoHex: hex || null,
        });
        enqueueSnackbar('Color creado', { variant: 'success' });
      }
      navigate('/inventario/fashion/colores');
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data || 'Error al guardar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2.5} component="form" onSubmit={handleSubmit}>
            <Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  startIcon={<ArrowBack />}
                  onClick={() => navigate('/inventario/fashion/colores')}
                  sx={{ textTransform: 'none' }}
                >
                  Colores
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {isEdit ? 'Editar color' : 'Nuevo color'}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Definí código, nombre y un color visual para identificarlo en matrices y listados.
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Código"
                  fullWidth
                  required
                  disabled={isEdit}
                  value={form.codigoColor}
                  onChange={(e) => setForm((f) => ({ ...f, codigoColor: e.target.value.toUpperCase() }))}
                  inputProps={{ maxLength: 20 }}
                  helperText={isEdit ? 'El código no se puede modificar' : 'Ej: AZ, NEG, RJ'}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Nombre"
                  fullWidth
                  required
                  value={form.nombreColor}
                  onChange={(e) => setForm((f) => ({ ...f, nombreColor: e.target.value }))}
                  inputProps={{ maxLength: 60 }}
                  helperText="Nombre comercial del color"
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                bgcolor: 'grey.50',
              }}
            >
              <Stack spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Color visual
                </Typography>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <Box
                    aria-label="Vista previa del color"
                    sx={{
                      width: { xs: '100%', sm: 88 },
                      height: 88,
                      borderRadius: 2,
                      bgcolor: hexValido && hexActual ? hexActual : '#EEEEEE',
                      border: '1px solid',
                      borderColor: 'grey.400',
                      backgroundImage:
                        !hexValido || !hexActual
                          ? 'repeating-conic-gradient(#e0e0e0 0% 25%, #fff 0% 50%) 50% / 16px 16px'
                          : undefined,
                      flexShrink: 0,
                      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04)',
                    }}
                  />

                  <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
                      <Tooltip title="Abrir selector del sistema">
                        <Box
                          component="label"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            px: 1.5,
                            py: 1,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            bgcolor: '#fff',
                            '&:hover': { borderColor: 'primary.main' },
                          }}
                        >
                          <Box
                            component="input"
                            type="color"
                            value={colorPickerValue}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              aplicarHex(e.target.value)
                            }
                            aria-label="Seleccionar color"
                            sx={{
                              width: 36,
                              height: 36,
                              p: 0,
                              border: 'none',
                              bgcolor: 'transparent',
                              cursor: 'pointer',
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                            Elegir color
                          </Typography>
                        </Box>
                      </Tooltip>

                      <TextField
                        label="Hex"
                        value={form.codigoHex || ''}
                        onChange={(e) => handleHexInput(e.target.value)}
                        onBlur={() => {
                          if (form.codigoHex) {
                            const n = normalizeHex(form.codigoHex);
                            if (isValidHex(n)) setForm((f) => ({ ...f, codigoHex: n }));
                          }
                        }}
                        error={Boolean(hexError)}
                        helperText={hexError || 'Formato #RRGGBB (opcional)'}
                        inputProps={{ maxLength: 7, spellCheck: false }}
                        sx={{ minWidth: { sm: 160 }, flex: 1 }}
                        InputProps={{
                          startAdornment: hexValido && hexActual ? (
                            <InputAdornment position="start">
                              <Box
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: 0.5,
                                  bgcolor: hexActual,
                                  border: '1px solid #bbb',
                                }}
                              />
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Paleta rápida (moda)
                  </Typography>
                  <Box
                    role="listbox"
                    aria-label="Paleta de colores sugeridos"
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(6, 1fr)',
                        sm: 'repeat(8, 1fr)',
                        md: 'repeat(12, 1fr)',
                      },
                      gap: 1,
                    }}
                  >
                    {PALETA_MODA.map((p) => {
                      const selected = hexActual === p.hex;
                      return (
                        <Tooltip key={p.hex} title={`${p.nombre} (${p.hex})`} arrow>
                          <Box
                            component="button"
                            type="button"
                            role="option"
                            aria-selected={selected}
                            aria-label={`${p.nombre} ${p.hex}`}
                            onClick={() => aplicarHex(p.hex, p.nombre)}
                            sx={{
                              width: '100%',
                              aspectRatio: '1',
                              borderRadius: 1.5,
                              bgcolor: p.hex,
                              border: selected ? '2px solid' : '1px solid',
                              borderColor: selected ? 'primary.main' : 'grey.400',
                              boxShadow: selected
                                ? '0 0 0 2px rgba(25, 118, 210, 0.25)'
                                : 'none',
                              cursor: 'pointer',
                              p: 0,
                              outline: 'none',
                              transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                              '&:hover': { transform: 'scale(1.06)' },
                              '&:focus-visible': {
                                boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.45)',
                              },
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              </Stack>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={form.estado === 'A'}
                  onChange={(e) => setForm((f) => ({ ...f, estado: e.target.checked ? 'A' : 'I' }))}
                />
              }
              label="Activo"
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => navigate('/inventario/fashion/colores')}>
                Cancelar
              </Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? <CircularProgress size={22} /> : 'Guardar'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ColorForm;
